import React, { useState, useCallback } from "react";
import { Controller } from "react-hook-form";
import CreatableSelect from "react-select/creatable";

export default function CreatableMultiSelectInput({
	name, // ex.: "canais" (string[])
	control, // useForm().control
	label = "",
	placeholder = "Selecione ou digite…",
	initialOptions = [], // [{ value, label }]
	className = "",
	errors, // objeto do RHF: formState.errors
	disabled = false,
}) {
	const [options, setOptions] = useState(
		initialOptions.length
			? initialOptions
			: [
					{ value: "insta", label: "Instagram" },
					{ value: "fb", label: "Facebook" },
					{ value: "tt", label: "Twitter/X" },
			  ]
	);
	const [inputValue, setInputValue] = useState("");

	const existsByLabel = useCallback(
		(txt) => options.some((o) => o.label.toLowerCase() === txt.toLowerCase()),
		[options]
	);

	// mapeia string[] -> option[]
	const toOptions = useCallback(
		(values) => (values || []).map((v) => options.find((o) => o.value === v) ?? { value: v, label: v }),
		[options]
	);

	// pega mensagem de erro (se houver)
	const fieldError = errors && name && errors[name] && errors[name].message ? String(errors[name].message) : "";

	return (
		<div className={`input-select-box d-flex flex-column mb-2 ${className}`}>
			<label htmlFor={name}>{label}</label>

			<Controller
				name={name}
				control={control}
				render={({ field: { value, onChange, onBlur, ref } }) => {
					const selectedOptions = toOptions(value);

					const createIfPending = () => {
						const txt = (inputValue || "").trim();
						if (!txt) return;

						if (existsByLabel(txt)) {
							const found = options.find((o) => o.label.toLowerCase() === txt.toLowerCase());
							const next = selectedOptions.some((o) => o.value === found.value)
								? selectedOptions
								: [...selectedOptions, found];
							onChange(next.map((o) => o.value));
						} else {
							const newOption = { value: txt.toLowerCase(), label: txt };
							setOptions((prev) => [...prev, newOption]);
							onChange([...(value || []), newOption.value]);
						}
						setInputValue("");
					};

					return (
						<CreatableSelect
							inputId={name}
							isMulti
							isDisabled={disabled}
							options={options}
							value={selectedOptions}
							onChange={(selected) => onChange((selected || []).map((o) => o.value))}
							onCreateOption={(txt) => {
								const newOption = { value: txt.toLowerCase(), label: txt };
								setOptions((p) => [...p, newOption]);
								onChange([...(value || []), newOption.value]);
								setInputValue("");
							}}
							inputValue={inputValue}
							onInputChange={(val, meta) => {
								if (meta.action !== "set-value") setInputValue(val);
							}}
							onBlur={(e) => {
								createIfPending(); // confirma texto pendente
								onBlur(); // mantém comportamento do RHF
							}}
							onKeyDown={(e) => {
								if (e.key === "Tab") createIfPending();
							}}
							placeholder={placeholder}
							closeMenuOnSelect={false}
							className="react-select-container"
							classNamePrefix="rs"
							styles={{
								// caixa de seleção
								control: (base, state) => ({
									...base,
									borderRadius: 8,
									borderColor: state.isFocused ? "#e9e6f7" : "#aaa",
									boxShadow: state.isFocused ? "0 0 0 1px rgba(2, 5, 7, 0.35)" : "none",
									"&:hover": { borderColor: "#a19cb4" },
									minHeight: 44,
								}),
								valueContainer: (base) => ({
									...base,
									padding: "6px 10px", // 👈 padding interno
									gap: 6, // espaço entre chips/input
								}),
								multiValue: (base) => ({
									...base,
									margin: 2, // chips não “colarem” na borda
									backgroundColor: "#e0f2fe",
									borderRadius: 9999,
								}),
								menu: (base) => ({
									...base,
									marginTop: 6, // separa o menu da caixa
								}),
								// tags (valores selecionados)
								multiValue: (base) => ({
									...base,
									backgroundColor: "#e0f2fe", // sky-100
									borderRadius: 9999,
								}),
								// margem externa do componente (afasta da “linha”)
								container: (base) => ({
									...base,
									marginTop: 2,
									marginBottom: 6,
								}),

								//valores dentro das tags
								multiValueLabel: (base) => ({
									...base,
									// color: "#0c4a6e", // sky-900
									color: "#333", // sky-900
									fontWeight: 200,
								}),
								// "x" para remover
								multiValueRemove: (base) => ({
									...base,
									color: "#0369a1", // sky-700
									":hover": { backgroundColor: "#bae6fd", color: "#0c4a6e" },
								}),
								// opções do menu
								option: (base, state) => ({
									...base,
									backgroundColor: state.isSelected
										? "#e5e7eb" // gray-200 (selecionada)
										: state.isFocused
										? "#e0e0e6" // gray-100 (hover)
										: "#ffffff",
									color: "#111827", // gray-900 (texto)
									cursor: "pointer",
									":active": { backgroundColor: "#e5e7eb" }, // clique
								}),

								placeholder: (base) => ({ ...base, color: "#64748b" }),
								input: (base) => ({ ...base, color: "#0f172a" }),
								menu: (base) => ({
									...base,
									borderRadius: 12,
									overflow: "hidden",
									boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
								}),
							}}
							// theme={(t) => ({
							// 	...t,
							// 	borderRadius: 12,
							// 	colors: {
							// 		...t.colors,
							// 		primary: "#0c0c0c", // sky-400
							// 		primary25: "#0c0c0c", // sky-100
							// 		neutral20: "#0c0c0c",
							// 		neutral30: "#7dd3fc",
							// 	},
							// })}
							ref={ref}
							// Se usar em modal, pode habilitar:
							// menuPortalTarget={document.body}
							// styles={{ ...styles, menuPortal: base => ({ ...base, zIndex: 9999 }) }}
						/>
					);
				}}
			/>

			{fieldError ? <p className="mt-1 text-sm text-red-600">{fieldError}</p> : null}
		</div>
	);
}
// <div className={`w-full ${className}`}>
// 	{label ? (
// 		<label htmlFor={name} className="text-sm">
// 			{label}
// 		</label>
// 	) : null}
